import React, { useEffect, useRef, useState } from "react";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/OrderSlice";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { showToast } from "src/utils/showToaster";
import AddOrderWrapper from "../Add/AddOrderWrapper";
import EditOrderWrapper from "../Edit/EditOrderWrapper";
import OrderListing from "./OrderListing";
import {
  useChangeOrderStatusByIdMutation,
  useChangeStatusOrderByIdMutation,
  useGetAllOrderDataQuery,
} from "src/services/OrderService";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import { Formik } from "formik";
import { capitalizeFirstLetter } from "src/utils/helper/stringHelper";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import moment from "moment";
import { usePermission } from "src/hooks/usePermission";

interface FormValues {
  status: string | { value: string; label: string };
}

export const orderStatus = [
  { value: "ORDER_ADDED", label: "Order Added" },
  { value: "ORDER_RECIEVED", label: "Order Received" },
  { value: "ORDER_PENDING", label: "Order Pending" },
  { value: "AWAITING", label: "Order Awaiting" },
  { value: "DISPATCHED", label: "Dispatched" },
  { value: "COMPLETED", label: "Completed" },
  { value: "REFUND_PENDING", label: "Refund Pending" },
  { value: "REFUND_COMPLETED", label: "Refund Completed" },
  { value: "CANCEL_REFUND_PENDING", label: "Cancel Refund Pending" },
  { value: "CANCEL_REFUND_COMPLETED", label: "Cancel Refund Completed" },
];

const statusOptionsMap: Record<string, string[]> = {
  PENDING: ["ORDER_ADDED", "ORDER_PENDING", "AWAITING"],
  PAID: ["ORDER_RECIEVED", "DISPATCHED", "COMPLETED"],
  FAILED: ["CANCEL_REFUND_PENDING", "CANCEL_REFUND_COMPLETED"],
  REFUNDED: ["REFUND_PENDING", "REFUND_COMPLETED"],
};

const OrderListingWrapper = () => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isOpenAddDialog,
    isOpenEditDialog,
    isTableLoading,
    dateFilter,
  } = useSelector((state: RootState) => state.order);

  const [changeStatusOrder] = useChangeStatusOrderByIdMutation();
  const [changeOrderStatus] = useChangeOrderStatusByIdMutation();

  const dialogRef = useRef<HTMLDialogElement>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useGetAllOrderDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["createdAt"],
    page: page,
    filterBy: [],
    dateFilter: {
      startDate: dateFilter.start_date
        ? formatedDateTimeIntoIst(dateFilter.start_date, "YYYY-MM-DD")
        : "",
      endDate: dateFilter.end_date
        ? moment(dateFilter?.end_date).format("YYYY-MM-DD")
        : dateFilter.start_date
          ? moment().format("YYYY-MM-DD")
          : "",
    },
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
  }, [data, isLoading, isFetching, dispatch]);

  const handleChangeStatusOrder = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Order ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusOrder({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleUpdateOrderStatus = (orderId: string) => {
    setSelectedOrderId(orderId);
    dialogRef.current?.showModal();
  };

  const updateOrderStatus = (statusCode: string) => {
    if (!selectedOrderId) return;

    return changeOrderStatus({
      id: selectedOrderId,
      body: { statusCode },
    })
      .then((res: any) => {
        if (res.error) {
          showToast("error", res?.error?.data?.message);
        } else {
          showToast("success", res?.data?.message);
        }
      })
      .finally(() => {
        dialogRef.current?.close();
      });
  };
  const moduleName = "ORDER";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");
  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Date",
      flex: "flex-[2_1_0%]",
      renderCell: (row: any) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-800">
            {moment(row.createdAt).format("DD-MMM-yyyy")}
          </span>
          <span className="text-gray-500 text-sm">{row._id}</span>
        </div>
      ),
    },

    {
      field: "userName",
      headerName: "User Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row.userName}</span>,
    },
    // {
    //   field: "couponCode",
    //   headerName: "Coupon Code",
    //   flex: "flex-[1_1_0%]",
    // },
    {
      field: "allOrderMrp",
      headerName: "Order Amount",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "orderAmt",
      headerName: "Final Amount Paid",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "orderStatus",
      headerName: "Order Status",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        const statusObj = orderStatus.find((o) => o.value === row.orderStatus);
        return <span>{statusObj ? statusObj.label : row.orderStatus}</span>;
      },
    },
    // {
    //   field: "paymentGateway",
    //   headerName: "Payment Gateway",
    //   flex: "flex-[1_1_0%]",
    //   renderCell: (row: any) => (
    //     <span>{capitalizeFirstLetter(row.paymentGateway)}</span>
    //   ),
    // },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <span>{capitalizeFirstLetter(row.paymentStatus)}</span>
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "center",
      renderCell: (row: any) => {
        const menuOptions: { label: string; onClick: () => void }[] = [];

        if (canActive) {
          menuOptions.push({
            label: "Active/Deactive",
            onClick: () => handleChangeStatusOrder(row?._id),
          });
        }

        if (canEdit) {
          menuOptions.push({
            label: "Update Status",
            onClick: () => handleUpdateOrderStatus(row?._id),
          });
        }

        return <ATMMenu options={menuOptions} />;
      },
    }

  ];

  return (
    <>
      <OrderListing
        columns={columns}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />

      {isOpenAddDialog && (
        <AddOrderWrapper onClose={() => dispatch(setIsOpenAddDialog(false))} />
      )}
      {isOpenEditDialog && (
        <EditOrderWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedOrderId={selectedOrderId || ""}
        />
      )}

      <dialog
        ref={dialogRef}
        className="rounded-lg shadow-xl w-full max-w-md p-6 border border-gray-200 bg-white relative"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">
            Update Status
          </h2>
          <button
            onClick={() => dialogRef.current?.close()}
            className="text-gray-400 hover:text-gray-600 font-semibold"
            aria-label="Close"
          >
            Cancel
          </button>
        </div>
        <Formik<FormValues>
          initialValues={{ status: "" }}
          enableReinitialize
          onSubmit={(values) => {
            const statusCode =
              typeof values.status === "string"
                ? values.status
                : values.status?.value;

            updateOrderStatus(statusCode);
          }}
        >
          {({ handleSubmit, setFieldValue, values }) => {
            const currentOrder = items.find((o) => o._id === selectedOrderId);
            const currentStatus = currentOrder?.paymentStatus || "";
            const allowedStatuses = statusOptionsMap[currentStatus] || [];

            const selectOptions = allowedStatuses.map((key) => ({
              value: key,
              label: orderStatus.find((o) => o.value === key)?.label || key,
            }));

            return (
              <form onSubmit={handleSubmit}>
                <ATMSelect
                  name="status"
                  value={values.status}
                  onChange={(value: any) => setFieldValue("status", value)}
                  options={selectOptions}
                  placeholder="Select status"
                />

                <div className="flex justify-end mt-4">
                  <ATMLoadingButton className="w-24" type="submit">
                    Update
                  </ATMLoadingButton>
                </div>
              </form>
            );
          }}
        </Formik>
      </dialog>
    </>
  );
};

export default OrderListingWrapper;
